import React, { useContext, useState, createContext } from "react"
import { useTranslation } from "react-i18next"
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, IconButton } from "@material-ui/core"
import { useTheme } from "@material-ui/core/styles"
import { CloseRounded as CloseIcon } from "@material-ui/icons"
import WarningSVG from "../../icons/WarningSVG"
import InfoSvg from "../../icons/InfoSVG"

const ConfirmationDialog = ({ open, title, message, onConfirm, onDismiss, btnText, dynamicClass = "confirmationDialog" }) => {
	const { t } = useTranslation("global")
	const theme = useTheme()

	return (
		<Dialog className={dynamicClass} open={open} onClose={onDismiss}>
			<DialogTitle
				disableTypography
				style={{
					...theme.typography.subtitle1,
					fontWeight: theme.typography.fontWeightMedium,
					paddingTop: 14,
					paddingRight: 24,
					paddingLeft: 24,
				}}
			>
				{title}
			</DialogTitle>
			<DialogContent>
				<DialogContentText style={{ ...theme.typography.body2 }}>{message}</DialogContentText>
			</DialogContent>
			<DialogActions style={{ padding: 16, borderTop: `1px solid ${theme.palette.grey[300]}` }}>
				<Button
					style={{
						fontSize: theme.typography.button.fontSize,
						textTransform: "capitalize",
					}}
					variant="outlined"
					size="small"
					onClick={onDismiss}
				>
					{t("cancel")}
				</Button>
				<Button
					color="secondary"
					variant="contained"
					onClick={onConfirm}
					size="small"
					style={{
						color: theme.typography.button.color,
						background: theme.dialogAction.backgroundColor,
						fontSize: theme.typography.button.fontSize,
						textTransform: "capitalize",
					}}
				>
					{btnText}
				</Button>
			</DialogActions>
		</Dialog>
	)
}

const ConfirmationDialogContext = createContext({})

const ConfirmationDialogProvider = ({ children }) => {
	const [dialogOpen, setDialogOpen] = useState(false)
	const [dialogConfig, setDialogConfig] = useState({})

	const openDialog = ({ title, message, actionCallback }) => {
		setDialogOpen(true)
		setDialogConfig({ title, message, actionCallback })
	}

	const resetDialog = () => {
		setDialogOpen(false)
		setDialogConfig({})
	}

	const onConfirm = () => {
		resetDialog()
		dialogConfig.actionCallback(true)
	}

	const onDismiss = () => {
		resetDialog()
		dialogConfig.actionCallback(false)
	}

	return (
		<ConfirmationDialogContext.Provider value={{ openDialog }}>
			<ConfirmationDialog
				open={dialogOpen}
				title={dialogConfig?.title}
				message={dialogConfig?.message}
				onConfirm={onConfirm}
				onDismiss={onDismiss}
			/>
			{children}
		</ConfirmationDialogContext.Provider>
	)
}

const useConfirmationDialog = () => {
	const { openDialog } = useContext(ConfirmationDialogContext)
	const getConfirmation = ({ ...options }) =>
		new Promise((res) => {
			openDialog({ actionCallback: res, ...options })
		})

	return { getConfirmation }
}

export default ConfirmationDialog
export { ConfirmationDialogProvider, useConfirmationDialog }
